import { stashDataSource } from '@/features/dataSources/dataSourcesSlice';
import { useWorkspace } from '@/features/workspaces/hooks';
import { DataSource, DataSourceFileConfig, DataSourceIDEnum } from '@/models/dataSource';
import { EStatus } from '@/models/fileUpload';
import { useAppDispatch, useAppSelector } from '@/reducers';
import { useSaveDataSourceMutation } from '@/services/dataSources';
import { FormikProps } from 'formik';
import { FC, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ColumnItem } from '../../../../types';
import { ColumnMapEditor, getHeadersFromColumnMap } from '../../../commons/ColumnMapEditor';

type FileDataSourceColumnMap = {
  onNextStep?: VoidFunction;
  onPrevStep?: VoidFunction;
  onCancel?: VoidFunction;
  isToCheckUploadSuccess?: boolean;
};

const MAX_ROWS = 4;

export const FileDataSourceColumnMapStep: FC<FileDataSourceColumnMap> = ({ onNextStep, onPrevStep, isToCheckUploadSuccess = false }) => {
  const dispatch = useAppDispatch();
  const workspace = useWorkspace();
  const fileUploadStatus = useAppSelector((state) => state.fileUploading.status);
  const { t } = useTranslation('data_source_edit');

  const [saveDataSource] = useSaveDataSourceMutation();
  const columnMapFormRef = useRef<FormikProps<ColumnItem[]>>(null);

  const dataSource = useAppSelector((state) => state.dataSources.edit?.dataSource) as Partial<DataSource<DataSourceFileConfig>> | undefined;
  const fileData = useAppSelector((state) => state.fileUploading.fileData);

  const fileHeader: string[] = fileData ? [...fileData.header] : [];
  const previewRows: string[][] = fileData
    ? fileData.isDefaultHeader
      ? [...fileData.preview.slice(0, MAX_ROWS).slice(1)]
      : [...fileData.preview.slice(0, MAX_ROWS)]
    : [];

  const isUploadSuccess = fileUploadStatus?.uploadingStatus === EStatus.SUCCESS;

  const handleConfirm = async (headers: ReturnType<typeof getHeadersFromColumnMap>, options?: { normalizeAddresses?: boolean }) => {
    if (!dataSource?.name || !dataSource.configuration) return;

    dispatch(stashDataSource({ configuration: { ...dataSource.configuration, headers } }));
    let finalConfiguration: Record<string, unknown> = { ...dataSource.configuration };

    if (dataSource.type.id === DataSourceIDEnum.SFTP) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { authMethod, ...rest } = finalConfiguration;
      finalConfiguration = rest;
    }

    if (dataSource.type.id === DataSourceIDEnum.CsvFile || dataSource.type.id === DataSourceIDEnum.EncryptedCsvFile) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { filename, ...rest } = finalConfiguration;
      finalConfiguration = rest;
    }

    const response = await saveDataSource({
      workspaceId: workspace.id,
      dataSource: {
        type: dataSource.type.id,
        name: dataSource.name,
        configuration: {
          ...finalConfiguration,
          type: dataSource.configuration.type,
          isDefaultHeader: dataSource.configuration.isDefaultHeader,
          headers,
          url: dataSource.configuration.url || fileUploadStatus?.data?.path,
          ...(options?.normalizeAddresses && { normalizeAddresses: true }),
        },
      },
    });

    if (!('error' in response && response.error)) {
      onNextStep?.();
    }
  };

  const handlePrev = () => {
    const columnMap = columnMapFormRef.current?.values;
    if (columnMap && dataSource?.configuration) {
      dispatch(stashDataSource({ configuration: { ...dataSource.configuration, headers: getHeadersFromColumnMap(columnMap) } }));
    }
    onPrevStep?.();
  };

  return (
    <ColumnMapEditor
      workspaceId={workspace.id}
      headers={fileHeader}
      previewRows={previewRows}
      formRef={columnMapFormRef}
      onConfirm={handleConfirm}
      onPrev={handlePrev}
      isConfirmDisabled={isToCheckUploadSuccess && !isUploadSuccess}
      showAddressNormalization
      confirmTooltipEnabled={isToCheckUploadSuccess && !isUploadSuccess}
      confirmTooltipContent={t('file_upload_issue')}
    />
  );
};
